/**
 * Demo incidents for ?mock=1 (ADR 0058): one of each detector (but the watch's own), open, acknowledged and fixing,
 * plus a resolved and a dismissed one that Needs you leaves out. Three repeat a derived Needs you
 * row on purpose (the stuck session, the dirty fix/voice-latency worktree, the failed landing of
 * feat/audit-csv), so the demo shows each said once. Built with the demo state, so the watch slice
 * is there from the first paint. Two are handled by Orgus (ORGU-92) and sit in the fold, uncounted.
 */
import { WATCH_TICK_MS, incidentId } from '@orgus/protocol';
import type { Incident, IncidentDetector, WatchState } from '@orgus/protocol';

const S = 1_000;
const MIN = 60 * S;
const H = 60 * MIN;

type Spec = Omit<Incident, 'id' | 'updatedAt' | 'lastSeenAt' | 'openedAt' | 'scope' | 'count'> & {
  ago: number;
  count?: number;
  scope?: Incident['scope'];
};

function inc(now: number, s: Spec): Incident {
  const { ago, count = 1, scope = {}, ...rest } = s;
  return { ...rest, id: incidentId(s.detector, s.key), scope, count, openedAt: now - ago, updatedAt: now - Math.min(ago, 2 * MIN), lastSeenAt: now - 12 * S };
}

export function mockWatch(now: number): WatchState {
  const incidents: Incident[] = [
    inc(now, {
      detector: 'refusal-storm',
      key: 'm-studio',
      severity: 'critical',
      state: 'open',
      title: 'Orgus refuses every tool call on studio',
      why: 'Agents on studio retry blind and stop. Pass the held requests back so each session asks in its own dialog.',
      evidence: [
        { label: 'Refusals in the last minute', value: 42 },
        { label: 'Sessions affected', value: 6 },
        { label: 'Hook', value: 'PreToolUse, http://127.0.0.1:4600' },
      ],
      action: { label: 'Pass requests back', kind: 'hand-back', target: 'm-studio' },
      responsible: { kind: 'machine', id: 'm-studio', label: 'studio' },
      scope: { machineId: 'm-studio' },
      ago: 14 * MIN,
      count: 3,
    }),
    inc(now, {
      detector: 'main-red',
      key: 'main',
      severity: 'critical',
      state: 'open',
      title: 'Main is red: two hub tests fail on 3f2a9c1',
      why: 'Every landing waits on a green main. The landing of session-titles broke them.',
      evidence: [
        { label: 'Failing', value: 'apps/hub stream.test.ts, approvals.test.ts' },
        { label: 'Commit', value: '3f2a9c1 Session titles in the drawer' },
      ],
      action: { label: 'Copy test command', kind: 'command', target: 'pnpm --filter @orgus/hub test' },
      responsible: { kind: 'branch', id: 'session-titles', label: 'session-titles' },
      scope: { main: true, branch: 'session-titles', projectId: 'p-0b44e8d1' }, // MOCK_IDS.orgus
      ago: 9 * MIN,
    }),
    inc(now, {
      detector: 'stuck-session',
      key: 'e7a90d13-foundation',
      severity: 'high',
      state: 'open',
      title: 'Build Orgus made no progress for 26 minutes',
      why: 'It shows working, but its build:audit agent has not moved and it waits on nothing.',
      evidence: [
        { label: 'Last transcript change', value: '26 min ago' },
        { label: 'Subagents running', value: 0 },
      ],
      action: { label: 'Open session', kind: 'open', target: '/?session=e7a90d13-foundation' },
      responsible: { kind: 'session', id: 'e7a90d13-foundation', label: 'Build Orgus' },
      scope: { sessionId: 'e7a90d13-foundation', machineId: 'm-studio' },
      ago: 26 * MIN,
    }),
    inc(now, {
      detector: 'landing-failed',
      key: 'feat/audit-csv',
      severity: 'high',
      state: 'open',
      title: 'The landing of feat/audit-csv failed on agent-ts tests',
      why: 'The branch is ready but never reached main, and its session has ended.',
      evidence: [
        { label: 'Failed check', value: 'pnpm test:affected, @orgus/agent-ts' },
        { label: 'Attempts', value: 2 },
      ],
      action: { label: 'Copy land command', kind: 'command', target: 'node tools/orgus-cli/bin/orgus.mjs land' },
      responsible: { kind: 'branch', id: 'feat/audit-csv', label: 'feat/audit-csv' },
      scope: { branch: 'feat/audit-csv' },
      ago: 41 * MIN,
      count: 2,
    }),
    inc(now, {
      detector: 'count-mismatch',
      key: 'inbox',
      severity: 'high',
      state: 'acknowledged',
      by: 'owner',
      title: 'The hub counts 4 waiting, the Inbox shows 1',
      why: 'Three requests wait where nobody sees them.',
      evidence: [
        { label: 'Hub waiting count', value: 4 },
        { label: 'Inbox rows', value: 1 },
      ],
      action: { label: 'Open Inbox', kind: 'open', target: '/inbox' },
      responsible: { kind: 'agent', id: 'orgus', label: 'Orgus' },
      ago: 18 * MIN,
    }),
    inc(now, {
      detector: 'hub-drift',
      key: 'hub-4600',
      severity: 'medium',
      state: 'fixing',
      by: 'orgus-watch-ui',
      note: 'Restarting follow-main after the merge.',
      title: 'The hub on 4600 runs 6 commits behind main',
      why: 'The console can call routes this hub does not have yet.',
      evidence: [
        { label: 'Hub build', value: 'a71c0de' },
        { label: 'Main', value: '4d6f3fc' },
        { label: 'follow-main', value: 'Stopped 52 min ago' },
      ],
      action: { label: 'Copy restart command', kind: 'command', target: 'node tools/orgus-cli/bin/orgus.mjs follow-main' },
      responsible: { kind: 'machine', id: 'm-studio', label: 'studio' },
      scope: { main: true, machineId: 'm-studio' },
      ago: 52 * MIN,
    }),
    inc(now, {
      detector: 'orphaned-work',
      key: 'C:/tmp/qawl-wt-voice-latency',
      severity: 'medium',
      state: 'open',
      title: 'fix/voice-latency holds 6 uncommitted changes with no session',
      why: 'The work exists only on disk; nothing will land it.',
      evidence: [
        { label: 'Uncommitted files', value: 6 },
        { label: 'Session ended', value: '2 h ago' },
      ],
      action: { label: 'Copy status command', kind: 'command', target: 'git -C C:/tmp/qawl-wt-voice-latency status' },
      responsible: { kind: 'branch', id: 'fix/voice-latency', label: 'fix/voice-latency' },
      scope: { branch: 'fix/voice-latency', worktree: 'C:\\tmp\\qawl-wt-voice-latency' },
      handling: { state: 'working', remedy: 'mission', text: 'Orgus is handling this: a mission lands the branch, or records why to drop it.', missionId: 'msn_demo_voice', attempt: 1, since: now - 20 * MIN },
      ago: 2 * H,
    }),
    inc(now, {
      detector: 'forgotten-process',
      key: 'm-studio:4931',
      severity: 'low',
      state: 'acknowledged',
      by: 'console-logo',
      title: 'A vite server on port 4931 has run for 3 hours',
      why: 'Its task landed at 11:20, and it holds the worktree folder open.',
      evidence: [
        { label: 'Process', value: 'node vite, pid 18244' },
        { label: 'Started by', value: 'console-logo' },
      ],
      action: { label: 'Open machine', kind: 'open', target: '/machines' },
      responsible: { kind: 'agent', id: 'console-logo', label: 'console-logo' },
      scope: { machineId: 'm-studio', branch: 'console-logo' },
      ago: 3 * H,
    }),
    inc(now, {
      detector: 'flaky-test',
      key: 'agents/agent-ts/src/watcher.test.ts',
      severity: 'low',
      state: 'open',
      title: 'watcher.test.ts failed 3 times this week, then passed',
      why: 'Each flake costs a landing a retry.',
      evidence: [{ label: 'Test', value: 'agents/agent-ts/src/watcher.test.ts' }],
      action: { label: 'Acknowledge', kind: 'ack' },
      responsible: { kind: 'agent', id: 'agent-ts', label: 'agent-ts' },
      handling: { state: 'working', remedy: 'none', text: 'Orgus starts a fix mission if it flakes again within a day.', since: now - H },
      ago: 3 * 24 * H,
      count: 3,
    }),
    inc(now, {
      detector: 'stale-failure',
      key: 'sessions',
      severity: 'low',
      state: 'open',
      title: '3 failed sessions older than a day are still unhandled',
      why: 'Old failures sit above live work on Sessions. Retry them or let them go.',
      evidence: [
        { label: 'Failed sessions', value: 3 },
        { label: 'Oldest', value: '2 days ago' },
      ],
      action: { label: 'Open sessions', kind: 'open', target: '/sessions' },
      responsible: { kind: 'person', id: 'owner', label: 'you' },
      ago: 26 * H,
    }),
    // Out of Needs you: resolved by the watch, and dismissed with a reason.
    {
      ...inc(now, {
        detector: 'stuck-session',
        key: 'b1f4c2e0-approvals',
        severity: 'high',
        state: 'resolved',
        title: 'Approvals inbox made no progress for 20 minutes',
        why: 'It waited on nothing.',
        evidence: [],
        action: { label: 'Open session', kind: 'open', target: '/?session=b1f4c2e0-approvals' },
        responsible: { kind: 'session', id: 'b1f4c2e0-approvals', label: 'Approvals inbox' },
        ago: 5 * H,
      }),
      resolvedAt: now - 4 * H,
      resolvedBy: 'watch',
    },
    inc(now, {
      detector: 'forgotten-process',
      key: 'm-mbp16:5173',
      severity: 'low',
      state: 'dismissed',
      by: 'owner',
      note: 'My own dev server.',
      title: 'A vite server on port 5173 has run for 9 hours',
      why: 'Its task ended this morning.',
      evidence: [],
      action: { label: 'Open machine', kind: 'open', target: '/machines' },
      responsible: { kind: 'person', id: 'owner', label: 'you' },
      ago: 9 * H,
    }),
  ];
  const detectors: IncidentDetector[] = ['refusal-storm', 'count-mismatch', 'hub-drift', 'landing-failed', 'main-red', 'flaky-test', 'orphaned-work', 'forgotten-process', 'stuck-session', 'stale-failure'];
  return {
    incidents: [...incidents].sort((a, b) => b.updatedAt - a.updatedAt),
    lastTickAt: now - 12 * S,
    tickMs: WATCH_TICK_MS,
    detectors: detectors.map((d) => ({ detector: d, lastRunAt: now - 12 * S, error: null })),
  };
}
